/**
 * The search box's field-prefix syntax, client side. (item 4)
 *
 * `title:emergency`, `id:1104`, `text:welding`, an exact `"phrase"`, and
 * `-excluded` words. The Advanced panel is a COMPOSER for exactly this syntax
 * — it writes into the same box the user could have typed into — so there is
 * one query language, one URL that describes any search however it was built,
 * and one thing for the backend to parse.
 *
 * That choice is what this module exists to support: it has to go BOTH ways.
 * `parseQuery` pulls a typed query apart so opening the panel shows what is
 * already in the box; `composeQuery` puts it back together on Apply.
 *
 * ROUND-TRIP HONESTY. Not every query decomposes: two exact phrases, or the
 * same prefix used twice, collapse into one panel field and would come back
 * different. `parseQuery` therefore checks its own work — it recomposes and
 * compares — and when the two disagree it hands the whole query back as plain
 * text in the "other words" box instead of showing a decomposition that
 * misrepresents it. The panel then still works; it just does not pretend to
 * have understood more than it did.
 *
 * The comparison is on the multiset of whitespace tokens, not on the string,
 * because compose emits its parts in a fixed order and `welding title:inspection`
 * is the same query as `title:inspection welding`. Nothing is rewritten until
 * the user presses Apply, so the worst case of a false match is that Apply
 * reorders the terms of a query the user is looking straight at.
 *
 * The alias vocabulary comes from searchRegistry.generated (and so from
 * search-registry.json), which is the same file the backend reads — a prefix
 * cannot exist in one and not the other.
 */
import { FIELD_PREFIXES } from '../config/searchRegistry.generated';

/** lowercased spelling a user may type -> the alias it belongs to */
const ALIAS_BY_PREFIX = {};
FIELD_PREFIXES.forEach((f) => {
  f.prefixes.forEach((p) => {
    ALIAS_BY_PREFIX[p.toLowerCase()] = f.alias;
  });
});

/** The spelling the panel WRITES for each alias (the first one declared). */
const CANONICAL = {};
FIELD_PREFIXES.forEach((f) => {
  CANONICAL[f.alias] = f.prefixes[0];
});

const PREFIX_RE = /^([A-Za-z_][A-Za-z0-9_]{0,15}):/;
const WS = /\s/;

/**
 * Split a query into `{neg, prefix, value, wrap}` tokens.
 *
 * Deliberately shallow: it understands a leading `-`, a `word:` prefix, and
 * three ways of delimiting a value (bare, "quoted", (grouped)). Everything
 * else — how terms combine, what a phrase means — belongs to edismax on the
 * server, and re-deciding it here is how the box and the results start
 * disagreeing.
 */
function tokenize(input) {
  const s = input || '';
  const out = [];
  let i = 0;
  while (i < s.length) {
    if (WS.test(s[i])) {
      i += 1;
      continue;
    }
    let neg = false;
    if (s[i] === '-' && i + 1 < s.length && !WS.test(s[i + 1])) {
      neg = true;
      i += 1;
    }
    let prefix = null;
    const m = PREFIX_RE.exec(s.slice(i));
    if (m) {
      prefix = m[1];
      i += m[0].length;
    }
    let value = '';
    let wrap = '';
    if (s[i] === '"') {
      const end = s.indexOf('"', i + 1);
      value = end === -1 ? s.slice(i + 1) : s.slice(i + 1, end);
      i = end === -1 ? s.length : end + 1;
      wrap = '"';
    } else if (s[i] === '(') {
      const end = s.indexOf(')', i + 1);
      value = end === -1 ? s.slice(i + 1) : s.slice(i + 1, end);
      i = end === -1 ? s.length : end + 1;
      wrap = '()';
    } else {
      const start = i;
      while (i < s.length && !WS.test(s[i])) i += 1;
      value = s.slice(start, i);
    }
    if (value !== '' || prefix) out.push({ neg, prefix, value, wrap });
  }
  return out;
}

/** Empty panel state. */
export const EMPTY = { fields: {}, phrase: '', exclude: '', rest: '' };

function decompose(term) {
  const fields = {};
  const phrases = [];
  const excludes = [];
  const rest = [];

  tokenize(term).forEach((t) => {
    const alias = t.prefix ? ALIAS_BY_PREFIX[t.prefix.toLowerCase()] : null;
    if (alias && !t.neg) {
      // A quoted field value keeps its quotes: `title:"a b"` is a phrase match
      // on the title and `title:(a b)` is two terms, and collapsing them would
      // change the query. Parens are pure grouping and are re-added by
      // `wrapValue`, so they are dropped here.
      const v = t.wrap === '"' ? `"${t.value}"` : t.value;
      fields[alias] = fields[alias] ? `${fields[alias]} ${v}` : v;
      return;
    }
    if (t.neg) {
      excludes.push(t.wrap === '"' ? `"${t.value}"` : t.value);
      return;
    }
    if (t.wrap === '"') {
      phrases.push(t.value);
      return;
    }
    // An unrecognised prefix is ordinary text — the backend's `uf` makes sure
    // Solr reads it that way too — so it stays glued to its value.
    rest.push(t.prefix ? `${t.prefix}:${t.value}` : t.value);
  });

  return {
    fields,
    phrase: phrases.join(' '),
    exclude: excludes.join(' '),
    rest: rest.join(' '),
  };
}

const wrapValue = (raw) => {
  const v = (raw || '').trim();
  if (!v) return '';
  if (/^".*"$/.test(v) || /^\(.*\)$/.test(v)) return v; // already delimited
  return WS.test(v) ? `(${v})` : v;
};

/** The panel's state -> the string that goes in the search box. */
export function composeQuery(state) {
  const { fields = {}, phrase = '', exclude = '', rest = '' } = state || {};
  const out = [];

  FIELD_PREFIXES.forEach((f) => {
    const v = wrapValue(fields[f.alias]);
    if (v) out.push(`${CANONICAL[f.alias]}:${v}`);
  });

  const p = (phrase || '').trim().replace(/"/g, '');
  if (p) out.push(`"${p}"`);

  const r = (rest || '').trim();
  if (r) out.push(r);

  (exclude || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .forEach((w) => out.push(w.startsWith('-') ? w : `-${w}`));

  return out.join(' ');
}

/** Multiset of whitespace tokens — see the round-trip note in the header. */
const tokenKey = (s) =>
  (s || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .sort()
    .join(' ');

/**
 * The search box's string -> the panel's state.
 *
 * Falls back to putting the whole query in `rest` when it cannot be taken
 * apart and put back together unchanged.
 */
export function parseQuery(term) {
  const t = (term || '').trim();
  if (!t) return { ...EMPTY, fields: {} };
  const parts = decompose(t);
  if (tokenKey(composeQuery(parts)) !== tokenKey(t)) {
    return { ...EMPTY, fields: {}, rest: t };
  }
  return parts;
}

/** Aliases this query names, in first-use order. Mirrors backend fields.expand. */
export function usedAliases(term) {
  const seen = [];
  tokenize(term).forEach((t) => {
    const alias = t.prefix ? ALIAS_BY_PREFIX[t.prefix.toLowerCase()] : null;
    if (alias && !t.neg && seen.indexOf(alias) === -1) seen.push(alias);
  });
  return seen;
}

/**
 * The same query with every field restriction removed — the escape hatch a
 * zero-result fielded search needs. `title:welding -inspection` becomes
 * `welding -inspection`: the words survive, the narrowing does not.
 */
export function stripPrefixes(term) {
  return tokenize(term)
    .map((t) => {
      const alias = t.prefix ? ALIAS_BY_PREFIX[t.prefix.toLowerCase()] : null;
      const value =
        t.wrap === '"'
          ? `"${t.value}"`
          : t.wrap === '()'
            ? t.value
            : t.value;
      const head = alias ? '' : t.prefix ? `${t.prefix}:` : '';
      return `${t.neg ? '-' : ''}${head}${value}`;
    })
    .filter(Boolean)
    .join(' ')
    .trim();
}

/**
 * The alias as a NOUN — "document titles", not "Title contains" — for the
 * zero-results line that has to say what was searched inside a sentence.
 */
export function aliasNoun(alias) {
  const f = FIELD_PREFIXES.find((x) => x.alias === alias);
  return f?.noun || f?.label || alias;
}
